import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ProposalModal from './ProposalModal';
import ProposalViewer from './ProposalViewer';

const { FiPlus, FiFileText, FiEye, FiEdit2, FiSend, FiCheckCircle, FiPenTool } = FiIcons;

const Proposals = () => {
  const { proposals, updateProposal } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [viewingProposal, setViewingProposal] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Signed': return 'text-purple-600 bg-purple-100';
      case 'Accepted': return 'text-green-600 bg-green-100';
      case 'Sent': return 'text-blue-600 bg-blue-100';
      case 'Draft': return 'text-gray-600 bg-gray-100';
      case 'Rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleSendProposal = (proposal) => {
    updateProposal(proposal.id, { status: 'Sent', sentAt: new Date() });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-800">Proposals</h1>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            setEditingProposal(null);
            setIsModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          <span>New Proposal</span>
        </motion.button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {proposals.map((proposal) => (
            <motion.div
              key={proposal.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiFileText} className="text-blue-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{proposal.title}</h3>
                    <p className="text-sm text-gray-500">{proposal.client}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(proposal.status)}`}>
                    {proposal.status}
                  </span>
                  {proposal.status === 'Signed' && (
                    <SafeIcon icon={FiPenTool} className="text-purple-500 text-sm" />
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Total Value:</span>
                  <span className="font-semibold text-gray-800">${proposal.totalValue?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-600">{proposal.createdAt?.toLocaleDateString()}</span>
                </div>
                {proposal.sentAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Sent:</span>
                    <span className="text-gray-600">{proposal.sentAt.toLocaleDateString()}</span>
                  </div>
                )}
                {proposal.signedAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Signed:</span>
                    <span className="text-gray-600">{proposal.signedAt.toLocaleDateString()}</span>
                  </div>
                )}
                {proposal.finalAmount && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Final Amount:</span>
                    <span className="font-semibold text-green-600">${proposal.finalAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Design Template Preview */}
              {proposal.designSettings && (
                <div className="mb-4 p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Template: {proposal.designSettings.template}</span>
                    <div 
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: proposal.designSettings.accentColor }}
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewingProposal(proposal)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
                >
                  <SafeIcon icon={FiEye} className="text-xs" />
                  <span>View</span>
                </motion.button>
                
                {proposal.status !== 'Signed' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setEditingProposal(proposal);
                      setIsModalOpen(true);
                    }}
                    className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded-lg text-sm hover:bg-blue-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <SafeIcon icon={FiEdit2} className="text-xs" />
                    <span>Edit</span>
                  </motion.button>
                )}
                
                {proposal.status === 'Draft' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSendProposal(proposal)}
                    className="flex-1 bg-green-100 text-green-700 py-2 px-3 rounded-lg text-sm hover:bg-green-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <SafeIcon icon={FiSend} className="text-xs" />
                    <span>Send</span>
                  </motion.button>
                )}
              </div>

              {/* Signature Preview */}
              {proposal.signature && proposal.clientInfo && (
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="text-xs text-purple-700 font-medium mb-1">
                    Signed by: {proposal.clientInfo.name}
                  </div>
                  {proposal.clientInfo.title && (
                    <div className="text-xs text-purple-600">
                      {proposal.clientInfo.title}
                    </div>
                  )}
                  <img 
                    src={proposal.signature} 
                    alt="Signature" 
                    className="h-8 mt-2 border border-purple-200 rounded bg-white"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {proposals.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiFileText} className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No proposals yet. Create your first proposal to get started.</p>
        </div>
      )}

      <ProposalModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProposal(null);
        }}
        proposal={editingProposal}
      />

      <ProposalViewer
        proposal={viewingProposal}
        onClose={() => setViewingProposal(null)}
      />
    </motion.div>
  );
};

export default Proposals;